import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { laboratoryInfo } from '../config';
import { useData } from '../contexts/DataContext';
import DocumentPreview from './DocumentPreview';
import { FaFileInvoice, FaPrint, FaCalendarAlt } from 'react-icons/fa';

/**
 * PeriodicInvoiceGenerator
 * Component that allows generating a Monthly / Periodic Detailed Summary Invoice for a practitioner
 */
const PeriodicInvoiceGenerator = ({ clientId, clientName, clientAddress, clientPhone, clientEmail, buttonLabel = "Facture Récapitulative / Relevé" }) => {
    const { invoices, orders, creditNotes, products } = useData();
    const [showModal, setShowModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    // Date range filter state
    // Default to current month from the 15th onwards, otherwise last month
    const today = new Date();
    const defaultPreset = today.getDate() >= 15 ? 'this_month' : 'last_month';
    const [periodPreset, setPeriodPreset] = useState(defaultPreset);
    const [excludePaid, setExcludePaid] = useState(true);
    const [excludedInvoiceIds, setExcludedInvoiceIds] = useState([]);
    const [excludedCreditIds, setExcludedCreditIds] = useState([]);

    // Compute dates helper
    const getPresetDates = (preset) => {
        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth();

        if (preset === 'this_month') {
            const start = new Date(y, m, 1);
            const end = new Date(y, m + 1, 0);
            return {
                start: start.toISOString().split('T')[0],
                end: end.toISOString().split('T')[0],
                label: `Mois en cours (${start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })})`
            };
        }
        if (preset === 'last_month') {
            const start = new Date(y, m - 1, 1);
            const end = new Date(y, m, 0);
            return {
                start: start.toISOString().split('T')[0],
                end: end.toISOString().split('T')[0],
                label: `Mois dernier (${start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })})`
            };
        }
        if (preset === 'two_months_ago') {
            const start = new Date(y, m - 2, 1);
            const end = new Date(y, m - 1, 0);
            return {
                start: start.toISOString().split('T')[0],
                end: end.toISOString().split('T')[0],
                label: `${start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
            };
        }
        if (preset === 'this_year') {
            const start = new Date(y, 0, 1);
            const end = new Date(y, 11, 31);
            return {
                start: start.toISOString().split('T')[0],
                end: end.toISOString().split('T')[0],
                label: `Année ${y}`
            };
        }
        if (preset === 'all_time') {
            return {
                start: '2000-01-01',
                end: '2099-12-31',
                label: `Toutes les factures (Global / Tout l'historique)`
            };
        }
        return { start: '', end: '', label: 'Période personnalisée' };
    };

    // Helper to compute standard global invoice number: FAC-YYYYMM-NOM
    const computeDefaultInvoiceNumber = (start, end, client) => {
        const baseDate = start ? new Date(start) : new Date();
        const yyyy = baseDate.getFullYear();
        const mm = String(baseDate.getMonth() + 1).padStart(2, '0');
        const cleanClient = (client || 'CLIENT')
            .replace(/^(dr\.?|docteur)\s+/i, '')
            .trim()
            .split(/\s+/)[0]
            .replace(/[^a-zA-Z0-9]/g, '')
            .toUpperCase();
        return `FAC-${yyyy}${mm}-${cleanClient}`;
    };

    const initialDates = getPresetDates(defaultPreset);
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);
    const [customInvoiceNumber, setCustomInvoiceNumber] = useState(() => computeDefaultInvoiceNumber(initialDates.start, initialDates.end, clientName));
    const [isCustomNumberEdited, setIsCustomNumberEdited] = useState(false);

    const handlePresetChange = (e) => {
        const val = e.target.value;
        setPeriodPreset(val);
        if (val !== 'custom') {
            const dates = getPresetDates(val);
            setStartDate(dates.start);
            setEndDate(dates.end);
            if (!isCustomNumberEdited) {
                setCustomInvoiceNumber(computeDefaultInvoiceNumber(dates.start, dates.end, clientName));
            }
        }
    };

    const handleStartDateChange = (val) => {
        setStartDate(val);
        if (!isCustomNumberEdited) {
            setCustomInvoiceNumber(computeDefaultInvoiceNumber(val, endDate, clientName));
        }
    };

    const normClientName = (clientName || '').trim().toLowerCase();
    const matchesClientObj = (item) => {
        if (clientId && (item.clientId === clientId || item.client_id === clientId)) return true;
        if (!normClientName) return false;
        return (
            (item.practitionerName || '').trim().toLowerCase() === normClientName ||
            (item.practitioner_name || '').trim().toLowerCase() === normClientName ||
            (item.clientName || '').trim().toLowerCase() === normClientName
        );
    };

    // Filter matching client invoices for the date period (before manual/paid exclusion)
    const periodInvoicesAll = useMemo(() => {
        if (!startDate || !endDate) return [];
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        return invoices.filter(inv => {
            if (!matchesClientObj(inv)) return false;
            const invDate = new Date(inv.date || inv.created_at || inv.createdAt);
            return invDate >= start && invDate <= end;
        }).sort((a, b) => new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt));
    }, [invoices, clientId, normClientName, startDate, endDate]);

    // Active invoices after applying "Exclude Paid" and manual row checkboxes
    const filteredInvoices = useMemo(() => {
        return periodInvoicesAll.filter(inv => {
            if (excludePaid && inv.status === 'Payée') return false;
            if (excludedInvoiceIds.includes(inv.id)) return false;
            return true;
        });
    }, [periodInvoicesAll, excludePaid, excludedInvoiceIds]);

    // Detect duplicate patient names or duplicate orderIds within the active list to warn against double-billing
    const duplicateWarnings = useMemo(() => {
        const patientCounts = {};
        const orderCounts = {};
        filteredInvoices.forEach(inv => {
            const pat = (inv.patientName || inv.patient_name || '').trim().toLowerCase();
            if (pat && pat !== '-') {
                patientCounts[pat] = (patientCounts[pat] || 0) + 1;
            }
            const oid = inv.orderId || inv.order_id;
            if (oid) {
                orderCounts[oid] = (orderCounts[oid] || 0) + 1;
            }
        });
        return { patientCounts, orderCounts };
    }, [filteredInvoices]);

    // Also get credit notes in period (approved credit notes)
    const periodCreditNotesAll = useMemo(() => {
        if (!startDate || !endDate) return [];
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        return creditNotes.filter(n => {
            if (!matchesClientObj(n)) return false;
            if (n.status === 'pending') return false;
            const nDate = new Date(n.date || n.createdAt);
            return nDate >= start && nDate <= end;
        });
    }, [creditNotes, clientId, normClientName, startDate, endDate]);

    const filteredCreditNotes = useMemo(() => {
        return periodCreditNotesAll.filter(n => !excludedCreditIds.includes(n.id));
    }, [periodCreditNotesAll, excludedCreditIds]);

    const toggleInvoiceSelection = (invId) => {
        setExcludedInvoiceIds(prev =>
            prev.includes(invId) ? prev.filter(id => id !== invId) : [...prev, invId]
        );
    };

    const toggleCreditSelection = (creditId) => {
        setExcludedCreditIds(prev =>
            prev.includes(creditId) ? prev.filter(id => id !== creditId) : [...prev, creditId]
        );
    };

    const paidCountInPeriod = periodInvoicesAll.filter(inv => inv.status === 'Payée').length;

    // Compute totals
    const totalInvoiced = filteredInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount || inv.total_amount || inv.total || 0)), 0);
    const totalCredits = filteredCreditNotes.reduce((sum, n) => sum + (parseFloat(n.amount || 0)), 0);
    const netTotalToPay = totalInvoiced - totalCredits;

    const effectiveInvoiceNumber = customInvoiceNumber && customInvoiceNumber.trim() !== ''
        ? customInvoiceNumber.trim()
        : computeDefaultInvoiceNumber(startDate, endDate, clientName);

    const safeClientName = clientName || 'Praticien';
    const formattedPeriodLabel = startDate && endDate
        ? `Du ${new Date(startDate).toLocaleDateString('fr-FR')} au ${new Date(endDate).toLocaleDateString('fr-FR')}`
        : '';
    const fileName = `facture-${effectiveInvoiceNumber}-${safeClientName.replace(/\s+/g, '_')}`;
    const targetId = `periodic-invoice-img-${clientId || 'unknown'}`;

    return (
        <>
            <button
                className="btn btn-primary fw-bold px-3 py-2 shadow-sm d-inline-flex align-items-center gap-2"
                onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
            >
                <FaFileInvoice /> {buttonLabel}
            </button>

            {/* Modal for selecting period (Rendered at document.body level via React Portal) */}
            {showModal && ReactDOM.createPortal(
                <div
                    tabIndex="-1"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(15, 23, 42, 0.75)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflowY: 'auto',
                        padding: '15px'
                    }}
                    onClick={() => setShowModal(false)}
                >
                    <div
                        className="modal-dialog modal-dialog-centered modal-lg my-0"
                        style={{ width: '100%', maxWidth: '800px' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content border-0 shadow-lg" style={{ backgroundColor: '#ffffff', color: '#0f172a', borderRadius: '12px', overflow: 'hidden' }}>
                            <div className="modal-header text-white p-3" style={{ backgroundColor: '#1e3a8a' }}>
                                <h5 className="modal-title fw-bold d-flex align-items-center m-0" style={{ color: '#ffffff', fontSize: '18px' }}>
                                    <FaFileInvoice className="me-2" /> Facture Récapitulative Mensuelle / Périodique
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body p-4" style={{ backgroundColor: '#ffffff', color: '#0f172a', maxHeight: '75vh', overflowY: 'auto' }}>
                                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', color: '#0f172a' }}>
                                    <h6 className="fw-bold mb-2" style={{ color: '#1e3a8a', fontSize: '16px' }}>Praticien : Dr. {clientName}</h6>
                                    {clientAddress && <div className="small mb-1" style={{ color: '#334155', fontWeight: '500' }}>📍 {clientAddress}</div>}
                                    {clientPhone && <span className="small me-3" style={{ color: '#334155', fontWeight: '500' }}>📞 {clientPhone}</span>}
                                    {clientEmail && <span className="small" style={{ color: '#334155', fontWeight: '500' }}>✉️ {clientEmail}</span>}
                                </div>

                                <h6 className="fw-bold mb-3" style={{ color: '#0f172a', fontSize: '15px' }}>
                                    <FaCalendarAlt className="me-2 text-primary" /> Choix de la Période :
                                </h6>

                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold" style={{ color: '#1e293b', fontSize: '13px' }}>Période pré-définie</label>
                                        <select
                                            className="form-select fw-semibold"
                                            style={{ backgroundColor: '#ffffff', color: '#0f172a', borderColor: '#94a3b8' }}
                                            value={periodPreset}
                                            onChange={handlePresetChange}
                                        >
                                            <option value="last_month">Mois dernier ({getPresetDates('last_month').label})</option>
                                            <option value="this_month">Mois en cours ({getPresetDates('this_month').label})</option>
                                            <option value="two_months_ago">Il y a 2 mois ({getPresetDates('two_months_ago').label})</option>
                                            <option value="this_year">Année complète ({getPresetDates('this_year').label})</option>
                                            <option value="all_time">Toutes les factures (Global / Tout l'historique)</option>
                                            <option value="custom">Période personnalisée...</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold" style={{ color: '#1e293b', fontSize: '13px' }}>
                                            N° de Facture Globale
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control fw-bold"
                                            style={{ backgroundColor: '#ffffff', color: '#1e3a8a', borderColor: '#94a3b8' }}
                                            value={customInvoiceNumber}
                                            onChange={(e) => {
                                                setCustomInvoiceNumber(e.target.value);
                                                setIsCustomNumberEdited(true);
                                            }}
                                            placeholder="Ex: FAC-202607-GOCIU"
                                        />
                                        <div className="small text-muted mt-1" style={{ fontSize: '11px' }}>
                                            Numéro légal de la facture récapitulative
                                        </div>
                                    </div>

                                    {periodPreset === 'custom' && (
                                        <>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold" style={{ color: '#1e293b', fontSize: '13px' }}>Date de début</label>
                                                <input
                                                    type="date"
                                                    className="form-control fw-semibold"
                                                    style={{ backgroundColor: '#ffffff', color: '#0f172a', borderColor: '#94a3b8' }}
                                                    value={startDate}
                                                    onChange={(e) => handleStartDateChange(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold" style={{ color: '#1e293b', fontSize: '13px' }}>Date de fin</label>
                                                <input
                                                    type="date"
                                                    className="form-control fw-semibold"
                                                    style={{ backgroundColor: '#ffffff', color: '#0f172a', borderColor: '#94a3b8' }}
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Anti-Double-Billing Filter & Results Summary Box */}
                                <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 px-1 gap-2">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`exclude-paid-${clientId}`}
                                            checked={excludePaid}
                                            onChange={(e) => setExcludePaid(e.target.checked)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <label className="form-check-label fw-semibold small" htmlFor={`exclude-paid-${clientId}`} style={{ color: '#1e293b', cursor: 'pointer' }}>
                                            Exclure automatiquement les dossiers déjà réglés ("Payée")
                                            {paidCountInPeriod > 0 && (
                                                <span className="badge bg-secondary ms-2">
                                                    {paidCountInPeriod} déjà payé{paidCountInPeriod > 1 ? 's' : ''} sur cette période
                                                </span>
                                            )}
                                        </label>
                                    </div>
                                    {excludedInvoiceIds.length > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary py-0 px-2"
                                            onClick={() => setExcludedInvoiceIds([])}
                                        >
                                            Réinclure les {excludedInvoiceIds.length} dossier(s) décoché(s)
                                        </button>
                                    )}
                                </div>

                                <div className="card border-0 mb-3" style={{ backgroundColor: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: '10px' }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                            <div>
                                                <span className="fw-bold me-2" style={{ color: '#1e3a8a', fontSize: '14px' }}>Dossiers retenus :</span>
                                                <span className="badge fs-6 fw-bold px-3 py-2" style={{ backgroundColor: '#1e3a8a', color: '#ffffff' }}>{filteredInvoices.length}</span>
                                                {totalCredits > 0 && (
                                                    <span className="ms-3 small fw-bold" style={{ color: '#16a34a' }}>
                                                        ({totalInvoiced.toFixed(2)} € travaux - {totalCredits.toFixed(2)} € d'avoir)
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <span className="fw-bold me-2" style={{ color: '#1e3a8a', fontSize: '14px' }}>Net à payer période :</span>
                                                <span className="fs-4 fw-bold" style={{ color: '#1d4ed8' }}>{netTotalToPay.toFixed(2)} €</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {periodCreditNotesAll.length > 0 && (
                                    <div className="mb-3 p-3 rounded" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                        <div className="fw-bold small mb-2" style={{ color: '#166534' }}>
                                            Avoirs / Réductions disponibles sur la période ({filteredCreditNotes.length}/{periodCreditNotesAll.length}) :
                                        </div>
                                        {periodCreditNotesAll.map(cn => {
                                            const isIncluded = !excludedCreditIds.includes(cn.id);
                                            return (
                                                <div key={cn.id} className="d-flex justify-content-between align-items-center py-1 border-bottom border-success border-opacity-10">
                                                    <div className="form-check mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={isIncluded}
                                                            onChange={() => toggleCreditSelection(cn.id)}
                                                            id={`cn-check-${cn.id}`}
                                                        />
                                                        <label className="form-check-label small fw-semibold" htmlFor={`cn-check-${cn.id}`} style={{ color: '#14532d', cursor: 'pointer' }}>
                                                            {new Date(cn.date || cn.createdAt).toLocaleDateString('fr-FR')} — <strong>{cn.creditNoteNumber || 'AVOIR'}</strong> : {cn.reference}
                                                        </label>
                                                    </div>
                                                    <span className="fw-bold small" style={{ color: isIncluded ? '#16a34a' : '#94a3b8', textDecoration: isIncluded ? 'none' : 'line-through' }}>
                                                        -{parseFloat(cn.amount || 0).toFixed(2)} €
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {periodInvoicesAll.length === 0 ? (
                                    <div className="alert text-center my-3 fw-semibold" style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                                        Aucune facture enregistrée pour ce praticien durant cette période ({formattedPeriodLabel}).
                                    </div>
                                ) : filteredInvoices.length === 0 ? (
                                    <div className="alert text-center my-3 fw-semibold" style={{ backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>
                                        Tous les dossiers de cette période ({periodInvoicesAll.length}) sont déjà réglés ("Payée"). Décochez "Exclure automatiquement les dossiers déjà réglés" ci-dessus si vous souhaitez réimprimer cette facture acquittée.
                                    </div>
                                ) : (
                                    <div className="table-responsive max-vh-40 border rounded mb-3" style={{ borderColor: '#cbd5e1' }}>
                                        <table className="table table-sm table-hover align-middle mb-0" style={{ backgroundColor: '#ffffff', color: '#0f172a' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#1e3a8a', color: '#ffffff' }}>
                                                    <th className="py-2 px-2 text-center" style={{ color: '#ffffff', width: '40px' }}>Incl.</th>
                                                    <th className="py-2 px-3" style={{ color: '#ffffff' }}>Date</th>
                                                    <th className="py-2 px-3" style={{ color: '#ffffff' }}>N° Facture / Cas</th>
                                                    <th className="py-2 px-3" style={{ color: '#ffffff' }}>Patient</th>
                                                    <th className="py-2 px-2 text-center" style={{ color: '#ffffff' }}>Statut</th>
                                                    <th className="py-2 px-3 text-end" style={{ color: '#ffffff' }}>Montant</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {periodInvoicesAll.map(inv => {
                                                    if (excludePaid && inv.status === 'Payée') return null;
                                                    const isIncluded = !excludedInvoiceIds.includes(inv.id);
                                                    const patKey = (inv.patientName || inv.patient_name || '').trim().toLowerCase();
                                                    const hasDuplicatePatient = isIncluded && patKey && duplicateWarnings.patientCounts[patKey] > 1;
                                                    const oid = inv.orderId || inv.order_id;
                                                    const hasDuplicateOrder = isIncluded && oid && duplicateWarnings.orderCounts[oid] > 1;

                                                    return (
                                                        <tr
                                                            key={inv.id}
                                                            style={{
                                                                borderBottom: '1px solid #e2e8f0',
                                                                color: '#0f172a',
                                                                opacity: isIncluded ? 1 : 0.45,
                                                                backgroundColor: hasDuplicateOrder ? '#fef2f2' : 'transparent'
                                                            }}
                                                        >
                                                            <td className="py-2 px-2 text-center">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={isIncluded}
                                                                    onChange={() => toggleInvoiceSelection(inv.id)}
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                            </td>
                                                            <td className="py-2 px-3" style={{ color: '#334155', fontWeight: '500' }}>{new Date(inv.date || inv.createdAt).toLocaleDateString('fr-FR')}</td>
                                                            <td className="py-2 px-3 fw-bold" style={{ color: '#1e3a8a' }}>{inv.invoiceNumber || inv.invoice_number || inv.caseNumber || inv.id.substr(0, 8)}</td>
                                                            <td className="py-2 px-3 fw-semibold" style={{ color: '#0f172a' }}>
                                                                {inv.patientName || inv.patient_name || '-'}
                                                                {(inv.patientReference || inv.patient_reference) ? <span className="small text-muted d-block" style={{ fontSize: '11px' }}>Réf: {inv.patientReference || inv.patient_reference}</span> : null}
                                                                {hasDuplicateOrder && (
                                                                    <span className="badge bg-danger ms-2" style={{ fontSize: '10px' }}>⚠️ Cas en doublon</span>
                                                                )}
                                                                {!hasDuplicateOrder && hasDuplicatePatient && (
                                                                    <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '10px' }} title="Ce patient apparaît plusieurs fois sur la période : vérifiez qu'il s'agit bien de deux travaux distincts">
                                                                        {duplicateWarnings.patientCounts[patKey]}x ce mois
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="py-2 px-2 text-center">
                                                                <span className={`badge ${inv.status === 'Payée' ? 'bg-success' : 'bg-primary bg-opacity-10 text-primary'}`} style={{ fontSize: '11px' }}>
                                                                    {inv.status === 'Payée' ? 'Payée' : 'À facturer'}
                                                                </span>
                                                            </td>
                                                            <td className="py-2 px-3 text-end fw-bold" style={{ color: '#0f172a' }}>{(parseFloat(inv.amount || inv.total_amount || 0)).toFixed(2)} €</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer p-3" style={{ backgroundColor: '#f8fafc', borderTop: '1px solid #cbd5e1' }}>
                                <button type="button" className="btn btn-secondary px-4 fw-bold" style={{ backgroundColor: '#64748b', borderColor: '#64748b' }} onClick={() => setShowModal(false)}>Fermer</button>
                                <button
                                    type="button"
                                    className="btn btn-success fw-bold px-4 shadow-sm"
                                    style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }}
                                    disabled={filteredInvoices.length === 0}
                                    onClick={() => {
                                        setShowModal(false);
                                        setShowPreview(true);
                                    }}
                                >
                                    <FaPrint className="me-2" /> Imprimer / Générer la Facture Récapitulative
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Document Preview Component */}
            {showPreview && (
                <DocumentPreview
                    targetId={targetId}
                    fileName={fileName}
                    onClose={() => setShowPreview(false)}
                />
            )}

            {/* Hidden Printable A4 Template */}
            <div id={targetId} style={{ display: 'none' }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '15mm 18mm',
                    color: '#0f172a',
                    width: '210mm',
                    boxSizing: 'border-box',
                    fontFamily: "'Segoe UI', Helvetica, Arial, sans-serif"
                }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '3px solid #1e3a8a', paddingBottom: '16px', marginBottom: '25px' }}>
                        <div>
                            <h2 style={{ margin: '0 0 6px 0', color: '#1e3a8a', fontSize: '22px', fontWeight: 'bold' }}>{laboratoryInfo.name}</h2>
                            <p style={{ margin: '2px 0', fontSize: '12px', color: '#334155' }}>{laboratoryInfo.address}</p>
                            <p style={{ margin: '2px 0', fontSize: '12px', color: '#334155' }}>Tél: {laboratoryInfo.phone}</p>
                            <p style={{ margin: '2px 0', fontSize: '12px', color: '#334155' }}>Email: {laboratoryInfo.email}</p>
                            <p style={{ margin: '2px 0', fontSize: '12px', color: '#334155' }}>SIRET: {laboratoryInfo.siret}</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{
                                backgroundColor: '#1e3a8a',
                                color: 'white',
                                padding: '8px 20px',
                                borderRadius: '6px',
                                display: 'inline-block',
                                marginBottom: '8px'
                            }}>
                                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    FACTURE
                                </h1>
                            </div>
                            <p style={{ margin: '3px 0 2px 0', fontSize: '14px', fontWeight: 'bold', color: '#1e3a8a' }}>
                                N° FACTURE : {effectiveInvoiceNumber}
                            </p>
                            <p style={{ margin: '3px 0', fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>
                                Période : {formattedPeriodLabel}
                            </p>
                            <p style={{ margin: '2px 0', fontSize: '12px', color: '#64748b' }}>
                                Date d'émission : {new Date().toLocaleDateString('fr-FR')}
                            </p>
                        </div>
                    </div>

                    {/* Practitioner Info Box */}
                    <div className="no-break" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '25px' }}>
                        <div style={{
                            width: '55%',
                            border: '1.5px solid #cbd5e1',
                            padding: '14px 18px',
                            borderRadius: '8px',
                            backgroundColor: '#f8fafc'
                        }}>
                            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                                Destinataire :
                            </div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#0f172a', marginBottom: '4px' }}>
                                Docteur {clientName}
                            </div>
                            {clientAddress && (
                                <div style={{ fontSize: '12px', color: '#334155', whiteSpace: 'pre-line', marginBottom: '4px' }}>
                                    {clientAddress}
                                </div>
                            )}
                            {clientPhone && <div className="small" style={{ fontSize: '12px', color: '#475569' }}>Tél: {clientPhone}</div>}
                            {clientEmail && <div className="small" style={{ fontSize: '12px', color: '#475569' }}>Email: {clientEmail}</div>}
                        </div>
                    </div>

                    {/* Table of Invoices / Cases */}
                    <div style={{ marginBottom: '25px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid #cbd5e1', paddingBottom: '4px' }}>
                            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 'bold', textTransform: 'uppercase', color: '#1e3a8a' }}>
                                Détail des Travaux et Dossiers Facturés
                            </h4>
                            <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>
                                {filteredInvoices.length} dossier{filteredInvoices.length > 1 ? 's' : ''}
                            </span>
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#1e3a8a', color: 'white' }}>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', width: '12%' }}>Date</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', width: '18%' }}>N° Dossier / Cas</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', width: '22%' }}>Patient</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', width: '33%' }}>Prestations / Actes réalisés</th>
                                    <th style={{ textAlign: 'right', padding: '8px 10px', width: '15%' }}>Montant TTC</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInvoices.map((inv, idx) => {
                                    // Try to get detailed items if present
                                    let itemsSummary = [];
                                    if (inv.items && Array.isArray(inv.items) && inv.items.length > 0) {
                                        itemsSummary = inv.items.map(item => `${item.description || item.name} ${item.quantity > 1 ? `(x${item.quantity})` : ''}`);
                                    } else {
                                        // Match with order
                                        const linkedOrder = orders.find(o => o.id === (inv.orderId || inv.order_id) || o.caseNumber === inv.caseNumber);
                                        if (linkedOrder && linkedOrder.teethConfig) {
                                            if (Array.isArray(linkedOrder.teethConfig)) {
                                                linkedOrder.teethConfig.forEach(line => {
                                                    const prod = products.find(p => p.id === line.productId);
                                                    if (prod) itemsSummary.push(prod.name);
                                                });
                                            } else {
                                                Object.values(linkedOrder.teethConfig).forEach(cfg => {
                                                    const prod = products.find(p => p.id === cfg.productId);
                                                    if (prod && !itemsSummary.includes(prod.name)) itemsSummary.push(prod.name);
                                                });
                                            }
                                        }
                                    }

                                    const formattedItems = itemsSummary.length > 0 ? itemsSummary.join(', ') : 'Prothèse Dentaire';

                                    return (
                                        <tr key={inv.id || idx} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                            <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                                                {new Date(inv.date || inv.createdAt).toLocaleDateString('fr-FR')}
                                            </td>
                                            <td style={{ padding: '8px 10px', fontWeight: 'bold', color: '#1e3a8a' }}>
                                                {inv.invoiceNumber || inv.invoice_number || inv.caseNumber || (inv.id ? inv.id.substr(0, 8).toUpperCase() : '')}
                                            </td>
                                            <td style={{ padding: '8px 10px', fontWeight: '500' }}>
                                                {inv.patientName || inv.patient_name || '-'}
                                                {(inv.patientReference || inv.patient_reference) ? <div style={{ fontSize: '10px', color: '#64748b' }}>Réf: {inv.patientReference || inv.patient_reference}</div> : null}
                                            </td>
                                            <td style={{ padding: '8px 10px', color: '#334155' }}>
                                                {formattedItems}
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 'bold' }}>
                                                {(parseFloat(inv.amount || inv.total_amount || 0)).toFixed(2)} €
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredCreditNotes.map((cn, cnIdx) => (
                                    <tr key={cn.id || `cn-${cnIdx}`} style={{ borderBottom: '1px solid #bbf7d0', backgroundColor: '#f0fdf4', color: '#166534' }}>
                                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontWeight: '600' }}>
                                            {new Date(cn.date || cn.createdAt).toLocaleDateString('fr-FR')}
                                        </td>
                                        <td style={{ padding: '8px 10px', fontWeight: 'bold', color: '#15803d' }}>
                                            {cn.creditNoteNumber || 'AVOIR'}
                                        </td>
                                        <td style={{ padding: '8px 10px', fontWeight: '600' }}>
                                            AVOIR DÉDUIT
                                        </td>
                                        <td style={{ padding: '8px 10px', color: '#166534', fontWeight: '500' }}>
                                            {cn.reference || 'Avoir commercial / Rectification'}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 'bold', color: '#15803d' }}>
                                            -{parseFloat(cn.amount || 0).toFixed(2)} €
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Financial Summary */}
                    <div className="no-break" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
                        <div style={{ width: '320px', border: '1.5px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden' }}>
                            <div style={{ backgroundColor: '#f1f5f9', padding: '10px 14px', fontWeight: 'bold', fontSize: '12px', borderBottom: '1px solid #cbd5e1', color: '#334155' }}>
                                RÉCAPITULATIF FACTURE
                            </div>
                            <div style={{ padding: '12px 14px', backgroundColor: 'white' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                                    <span style={{ color: '#64748b' }}>Total Travaux/Factures HT:</span>
                                    <span style={{ fontWeight: '600' }}>{totalInvoiced.toFixed(2)} €</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                                    <span style={{ color: '#64748b' }}>TVA (0%):</span>
                                    <span>0.00 €</span>
                                </div>
                                {totalCredits > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px', color: '#16a34a' }}>
                                        <span>Total Avoirs déduits:</span>
                                        <span>-{totalCredits.toFixed(2)} €</span>
                                    </div>
                                )}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    paddingTop: '10px',
                                    borderTop: '2px solid #1e3a8a',
                                    marginTop: '8px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    color: '#1e3a8a'
                                }}>
                                    <span>NET À PAYER :</span>
                                    <span>{netTotalToPay.toFixed(2)} €</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment Terms & Footer */}
                    <div className="no-break" style={{
                        marginTop: 'auto',
                        paddingTop: '16px',
                        borderTop: '1px solid #e2e8f0',
                        fontSize: '11px',
                        color: '#475569',
                        lineHeight: '1.5'
                    }}>
                        <div style={{ fontWeight: 'bold', color: '#1e3a8a', marginBottom: '4px' }}>Conditions de règlement :</div>
                        <p style={{ margin: '0 0 4px 0' }}>• Règlement à 30 jours à compter de la date d'émission de la présente facture récapitulative.</p>
                        <p style={{ margin: '0 0 10px 0' }}>• En cas de virement bancaire, merci de mentionner le nom du praticien et la période concernée.</p>
                        
                        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '10px', color: '#94a3b8' }}>
                            {laboratoryInfo.name} - SIRET : {laboratoryInfo.siret} - TVA : {laboratoryInfo.tva || 'Exonéré'}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PeriodicInvoiceGenerator;
